import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { btnLight } from "./api";

// Square, hairline dropdown used by the command bar. Items: { key, label, icon: Icon, onClick, disabled, title, hint, danger, busy }.
export function DropMenu({ label, icon: Icon, items, testid, className = "", align = "left", disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const anyBusy = items.some((i) => i.busy);
  return (
    <span className="relative inline-flex" ref={ref} data-testid={testid}>
      <button type="button" disabled={disabled} onClick={() => setOpen((v) => !v)} className={`${btnLight} ${className}`} data-testid={`${testid}-toggle`} aria-haspopup="menu" aria-expanded={open}>
        {anyBusy ? <Loader2 size={12} className="animate-spin" /> : Icon ? <Icon size={12} /> : null} {label} <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className={`absolute z-40 top-full mt-1 min-w-[280px] bg-white border border-[#E8E1D2] shadow-[0_12px_32px_-12px_rgba(15,24,37,0.35)] py-1 ${align === "right" ? "right-0" : "left-0"}`} data-testid={`${testid}-items`}>
          {items.map((it) => {
            const ItemIcon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                disabled={it.disabled || it.busy}
                title={it.title || ""}
                onClick={() => { setOpen(false); it.onClick(); }}
                className={`w-full text-left px-3.5 py-2.5 flex items-start gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FAF7F1] ${it.danger ? "text-[#C84C21]" : "text-[#0F1825]"}`}
                data-testid={it.testid || `${testid}-${it.key}`}
              >
                {it.busy ? <Loader2 size={13} className="animate-spin mt-0.5 shrink-0" /> : ItemIcon ? <ItemIcon size={13} className={`mt-0.5 shrink-0 ${it.danger ? "text-[#C84C21]" : "text-[#A09680]"}`} /> : null}
                <span className="min-w-0">
                  <span className="block text-[13px] leading-snug">{it.label}</span>
                  {it.hint && <span className="block text-[11px] text-[#5C6470] leading-snug mt-0.5">{it.hint}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

export function Tabs({ tabs, active, onChange, testid = "tabs" }) {
  return (
    <div className="flex items-end gap-1 border-b border-[#E8E1D2] overflow-x-auto" role="tablist" data-testid={testid}>
      {tabs.map((t) => {
        const Icon = t.icon;
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            className={`-mb-px inline-flex items-center gap-2 px-4 py-3 border-b-2 font-mono text-[10.5px] uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${on ? "border-[#C84C21] text-[#0F1825]" : "border-transparent text-[#5C6470] hover:text-[#0F1825]"}`}
            data-testid={`${testid}-${t.key}`}
          >
            {Icon && <Icon size={12} className={on ? "text-[#C84C21]" : "text-[#A09680]"} />}
            {t.label}
            {t.count !== undefined && t.count !== null && <span className={`font-mono text-[10px] px-1.5 py-0.5 ${on ? "bg-[#0F1825] text-white" : "bg-[#F0EBDC] text-[#5C6470]"}`}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
